import { _ as __rest } from './_tslib.js';
import { jsx } from 'react/jsx-runtime';
import { enforceCase, generateClasses, abbreviate } from '@ids/tools';
import { s as styleInject } from './style-inject.es.js';

var css_248z = ".ids-avatar {\n  -webkit-box-align: center;\n      -ms-flex-align: center;\n          align-items: center;\n  background-color: var(--ids_color_text_body_01);\n  border: var(--ids_size_border_small) solid var(--ids_color_contrast_onLight);\n  border-radius: 50%;\n  color: var(--ids_color_contrast_onDark);\n  display: -webkit-inline-box;\n  display: -ms-inline-flexbox;\n  display: inline-flex;\n  font: var(--ids_textStyle_f02_s14_h20_wrg);\n  height: var(--ids_size_general_32x);\n  -webkit-box-pack: center;\n      -ms-flex-pack: center;\n          justify-content: center;\n  line-height: 1;\n  overflow: hidden;\n  text-align: center;\n  width: var(--ids_size_general_32x);\n}\n.ids-avatar--on-brand {\n  background-color: var(--ids_color_contrast_onBrand_primary);\n  border-color: var(--ids_color_contrast_onBrand_primary);\n  color: var(--ids_color_action_neutral_variant);\n}\n.ids-avatar__icon {\n  font: var(--ids_textStyle_icon_small);\n}\n.ids-avatar__img {\n  height: 100%;\n  -o-object-fit: cover;\n     object-fit: cover;\n  width: 100%;\n}\n.ids-avatar--small {\n  font: var(--ids_textStyle_f02_s12_h18_wrg);\n  height: var(--ids_size_general_24x);\n  width: var(--ids_size_general_24x);\n}\n.ids-avatar--small .ids-avatar__icon {\n  font: var(--ids_textStyle_icon_small);\n}\n.ids-avatar--large {\n  font: var(--ids_textStyle_f02_s20_h26_wrg);\n  height: var(--ids_size_general_48x);\n  width: var(--ids_size_general_48x);\n}\n.ids-avatar--large .ids-avatar__icon {\n  font: var(--ids_textStyle_icon_medium);\n}\n.ids-avatar--xlarge {\n  font: var(--ids_textStyle_f02_s24_h32_wrg);\n  height: var(--ids_size_general_64x);\n  width: var(--ids_size_general_64x);\n}\n.ids-avatar--xlarge .ids-avatar__icon {\n  font: var(--ids_textStyle_icon_large);\n}\n.ids-avatar--credit-card-brand {\n  background: none;\n  border: none;\n  border-radius: 0;\n  color: var(--ids_color_action_neutral_base);\n}\n.ids-avatar--credit-card-brand .ids-avatar__icon {\n  font: var(--ids_textStyle_icon_medium);\n}\n.ids-avatar--credit-card-brand .ids-avatar__img {\n  -o-object-fit: scale-down;\n     object-fit: scale-down;\n}\n.ids-avatar--credit-card-brand.ids-avatar--on-brand .ids-avatar__icon {\n  color: var(--ids_color_contrast_onBrand_primary);\n}\n.ids-avatar--credit-card-brand.ids-avatar--small .ids-avatar__icon {\n  font: var(--ids_textStyle_icon_small);\n}\n.ids-avatar--credit-card-brand.ids-avatar--large .ids-avatar__icon {\n  font: var(--ids_textStyle_icon_large);\n}\n.ids-avatar--credit-card-brand.ids-avatar--xlarge .ids-avatar__icon {\n  font: var(--ids_textStyle_icon_xLarge);\n}";
styleInject(css_248z);

/**
 * @category Component
 *
 * @example
 * ```ts
 * import { IdsAvatar } from '@ids/react';
 * ```
 */
const IdsAvatar = _a => {
  var {
      className = '',
      size,
      contrast,
      icon = 'usuario_outline',
      text = '',
      src = '',
      alt = ''
    } = _a,
    props = __rest(_a, ["className", "size", "contrast", "icon", "text", "src", "alt"]);
  const ariaLabel = props['aria-label'];
  const role = ariaLabel && 'img';
  const upperSize = enforceCase(size, 'upperCase');
  const classes = generateClasses({
    'ids-avatar': true,
    'ids-avatar--small': upperSize === 'S',
    'ids-avatar--large': upperSize === 'L',
    'ids-avatar--xlarge': upperSize === 'XL',
    'ids-avatar--on-brand': contrast === 'onBrand',
    [className]: className
  });
  const render = {
    text: jsx("span", Object.assign({
      className: "ids-avatar__text",
      "aria-hidden": "true"
    }, {
      children: abbreviate(text)
    })),
    icon: jsx("span", Object.assign({
      className: "ids-avatar__icon",
      "aria-hidden": "true"
    }, {
      children: icon
    })),
    img: jsx("img", {
      className: "ids-avatar__img",
      src: src,
      alt: alt
    })
  };
  return jsx("div", Object.assign({
    className: classes,
    role: role,
    "aria-hidden": !ariaLabel && !src
  }, props, {
    children: text ? render.text : src ? render.img : render.icon
  }));
};

export { IdsAvatar as I };
//# sourceMappingURL=avatar.js.map
